import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { ArrowsRightLeftIcon } from '@heroicons/react/24/outline'

import { Avatar } from '@authy/lib-client/components'
import { env } from '@authy/lib-client/env'
import { Dropdown, Menu } from '@authy/lib-web/components'

import { adminAppState } from '@/appState'
import { itemHoverActiveClasName, itemActiveClassName } from '@/App/Navigation/Sidebar/utils'

type AppTenantProps = {
    collapsed: boolean
    onActivate: () => void
}

const isRootApp = () => {
    const appUrl = env.VITE_PRODUCT_URL_ROOT
    const subPaths = env.VITE_PRODUCT_URL_SUBPATHS

    if (window.location.href.indexOf(appUrl) !== 0) {
        return false
    }

    return !subPaths.split(',').some((subPath: string) => {
        return window.location.href.indexOf(`${appUrl}/${subPath}`) === 0
    })
}

export const TenantMenu = observer(({ collapsed, onActivate }: AppTenantProps) => {
    const { t } = useTranslation()

    const { tenant, application, applications } = adminAppState

    if (!tenant || !application) {
        return null
    }

    const appUrl = env.VITE_PRODUCT_URL_ROOT

    const appLink: any = {}
    if (isRootApp()) {
        appLink.to = '/'
    } else {
        appLink.href = `${appUrl}/user/start`
    }

    return (
        <Dropdown
            dropdownArrow={collapsed ? 'none' : 'hover'}
            className={classNames('flex w-full items-center gap-x-3 rounded-md text-left', itemHoverActiveClasName, {
                'h-12 px-2': !collapsed,
                'justify-center p-1': collapsed,
            })}
            activeClassName={itemActiveClassName}
            menuItems={
                <>
                    {applications
                        .filter(({ url }) => window.location.href.indexOf(url) < 0)
                        .map(({ key, url, name, Icon, bgColorClassName }) => (
                            <Menu.Item key={key} href={url}>
                                <div className="flex items-center gap-x-3">
                                    <Icon
                                        className={classNames(
                                            'h-8 w-8 rounded-md bg-gradient-to-r p-1.5 shadow-md',
                                            bgColorClassName
                                        )}
                                    />
                                    <div>{name}</div>
                                </div>
                            </Menu.Item>
                        ))}
                    <Menu.ItemDivider />
                    <Menu.Item Icon={ArrowsRightLeftIcon} {...appLink} onClick={onActivate}>
                        {t('app.header.switchTenant')}
                    </Menu.Item>
                </>
            }
        >
            <Avatar
                className={classNames('rounded bg-white', { 'size-8': !collapsed, 'size-7': collapsed })}
                src={tenant.avatar?.resource}
                name={tenant.name}
                variant="shape"
            />
            {!collapsed && <div className="flex-1 font-medium">{tenant.name}</div>}
        </Dropdown>
    )
})
